import React, { useRef, useEffect } from 'react';
import Editor from '@monaco-editor/react';
import { CircularProgress, Box, Button, Stack, Chip, Tooltip } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import GetAppIcon from '@mui/icons-material/GetApp';
import './CodeViewer.css';

interface CodeViewerProps {
  code: string;
  isLoading: boolean;
  error: string | null;
  modelType: string;
  configPath: string;
  onCopy: () => Promise<boolean>;
  onDownload: () => void;
}

/**
 * CodeViewer Component
 *
 * A Monaco Editor-based code viewer for displaying complete, runnable training code.
 * Features:
 * - Syntax highlighting (Python)
 * - Dark theme matching app aesthetic
 * - Copy to clipboard
 * - Download as .py file
 * - Line numbers, code folding, minimap
 */
export const CodeViewer: React.FC<CodeViewerProps> = ({
  code,
  isLoading,
  error,
  modelType,
  configPath,
  onCopy,
  onDownload,
}) => {
  const [copySuccess, setCopySuccess] = React.useState(false);
  const editorRef = useRef<any>(null);

  const handleCopy = async () => {
    const success = await onCopy();
    if (success) {
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    }
  };

  const handleEditorMount = (editor: any) => {
    editorRef.current = editor;
    // Set editor to read-only mode
    editor.updateOptions({ readOnly: true });
  };

  if (error) {
    return (
      <Box className="code-viewer-error">
        <h3>Error Loading Code</h3>
        <p>{error}</p>
      </Box>
    );
  }

  if (isLoading) {
    return (
      <Box className="code-viewer-loading">
        <CircularProgress />
        <p>Generating training code...</p>
      </Box>
    );
  }

  return (
    <Box className="code-viewer-container">
      {/* Header with metadata and controls */}
      <Box className="code-viewer-header">
        <Box className="code-viewer-header-left">
          <h2>Training Code</h2>
          <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
            <Chip
              label={`Model: ${modelType.toUpperCase()}`}
              color="primary"
              variant="outlined"
              size="small"
            />
            <Chip
              label={`Config: ${configPath}`}
              variant="outlined"
              size="small"
            />
          </Stack>
        </Box>

        {/* Action buttons */}
        <Stack direction="row" spacing={1} className="code-viewer-actions">
          <Tooltip title={copySuccess ? 'Copied!' : 'Copy to clipboard'}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<ContentCopyIcon />}
              onClick={handleCopy}
              sx={{
                backgroundColor: copySuccess ? 'rgba(76, 175, 80, 0.1)' : undefined,
                borderColor: copySuccess ? '#4caf50' : undefined,
                color: copySuccess ? '#4caf50' : undefined,
              }}
            >
              {copySuccess ? 'Copied!' : 'Copy'}
            </Button>
          </Tooltip>
          <Button
            variant="contained"
            size="small"
            startIcon={<GetAppIcon />}
            onClick={onDownload}
          >
            Download
          </Button>
        </Stack>
      </Box>

      {/* Monaco Editor */}
      <Box className="code-viewer-editor">
        <Editor
          height="100%"
          defaultLanguage="python"
          value={code}
          theme="vs-dark"
          onMount={handleEditorMount}
          options={{
            readOnly: true,
            fontSize: 13,
            fontFamily: "'Courier New', monospace",
            lineNumbers: 'on',
            lineNumbersMinChars: 3,
            wordWrap: 'on',
            minimap: { enabled: true },
            folding: true,
            foldingHighlight: true,
            automaticLayout: true,
            scrollBeyondLastLine: false,
            tabSize: 4,
            insertSpaces: true,
            smoothScrolling: true,
            mouseWheelZoom: true,
          }}
        />
      </Box>

      {/* Footer with info */}
      <Box className="code-viewer-footer">
        <small>
          This is a complete, runnable Python script. It includes model architecture, data loading,
          configuration, and training loop.
        </small>
      </Box>
    </Box>
  );
};

export default CodeViewer;
